import { CSmartTable } from "@coreui/react-pro";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  capitalizeWords,
  changeGMTFormate,
  StatusConversion,
} from "../../Components/allFunctions";
import Dropdown from "react-bootstrap/Dropdown";
import DropdownButton from "react-bootstrap/DropdownButton";

import Constants from "../../Constants";
import { authFetch } from "../../utils/authFetch";

const ADMIN_ENDPOINT = Constants.AUTH_BASE_URL + "/api/v1/admin";

const OrdersPage = (props) => {
  const lang = props.Language;
  // const [fullfillesData, setFullfilledData] = useState([]);
  // const [unFullfillesData, setUnFullfilledData] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const [accessToken, setAccessToken] = useState("");
  const [OrderStatus, setOrderStatus] = useState("");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [totalOrders, setTotalOrders] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");

  // Multi-column filter states
  const [filters, setFilters] = useState({
    fulfillmentStatus: [],
    tags: [],
    tagMode: "include", // include | exclude
    customerName: "",
    items: "",
    additionalInfo: [], // User Data filter: Uploaded | Data Missing | Scan Missing | Scan and Data Missing
  });

  // Collapsible filter state
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(false);

  const navigate = useNavigate();
  const changeHandler = (e) => {
    setOrderStatus(e.target.value);
    navigate(e.target.value);
  };
  const customItemsPerPageLabel =
    lang === "en" ? "Items per Page:" : "项目/页 : ";
  const customTableFilterLabel = lang === "en" ? "Filter" : "筛选";

  const USER_DATA_OPTIONS = [
    "Uploaded",
    "Data Missing",
    "Scan Missing",
    "Scan and Data Missing",
  ];

  const columns = [
    {
      key: "id",
      label: "ID",
      filter: false,
      sorter: false,
    },
    {
      key: "OrderName",

      label: lang === "en" ? "Order" : "订单",
    },
    {
      key: "CreatedAt",
      label: lang === "en" ? "Date" : "日期",
    },
    {
      key: "CustomerName",
      label: lang === "en" ? "Customer" : "客户",
    },
    {
      key: "FulfillmentStatus",
      label: lang === "en" ? "Fulfillment Status" : "进度状态",
    },
    {
      key: "Items",
      label: lang === "en" ? "Items" : "项目",
    },
    {
      key: "Tag",
      label: lang === "en" ? "Order Tags" : "供应商",
    },
    {
      key: "additionalInfo",
      label: lang === "en" ? "User Data" : "附加資訊",
      sorter: true,
    },
    {
      // Whether the trainer asked the factory to change anything about any pair
      // in this order. A column rather than something you find by opening the
      // order: a modified order is made differently, and the floor has to know
      // that from the queue, not after it has already been picked up.
      key: "modifications",
      label: lang === "en" ? "Modifications" : "修改",
      sorter: true,
    },
    {
      key: "messageCount",
      label: lang === "en" ? "Message Count" : "消息数",
    },
  ];

  const getAllOrders = (page = currentPage, itemsPerPage = perPage, search = searchTerm, activeFilters = filters) => {
    try {
      setLoading(true);

      let url = `${ADMIN_ENDPOINT}/orders?page=${page}&per_page=${itemsPerPage}`;
      if (search) {
        url += `&search=${encodeURIComponent(search)}`;
      }
      // Server-side advanced filters
      if (activeFilters.fulfillmentStatus.length > 0) {
        const statuses = activeFilters.fulfillmentStatus.filter(s => s !== "OTHER");
        statuses.forEach(s => { url += `&production_statuses[]=${encodeURIComponent(s)}`; });
      }
      if (activeFilters.tags.length > 0) {
        activeFilters.tags.forEach(t => { url += `&tags[]=${encodeURIComponent(t)}`; });
        url += `&tag_mode=${activeFilters.tagMode}`;
      }
      if (activeFilters.customerName) {
        url += `&customer_name=${encodeURIComponent(activeFilters.customerName)}`;
      }
      if (activeFilters.additionalInfo.length > 0) {
        activeFilters.additionalInfo.forEach(s => { url += `&user_data_statuses[]=${encodeURIComponent(s)}`; });
      }

      authFetch(url)
        .then((response) => response.json())
        .then((data) => {
          const meta = data.meta || {};
          setCurrentPage(meta.page || 1);
          setTotalPages(meta.total_pages || 1);
          setTotalOrders(meta.total || 0);
          setPerPage(meta.per_page || itemsPerPage);

          // Map new backend response to the format the UI expects
          const processedOrders = (data.orders || []).map((order) => {
            const additionalInfo = order.user_data_status || "";
            let className = "";
            if (additionalInfo === "Uploaded") {
              className = "scan-status scan-status-uploaded";
            } else if (additionalInfo) {
              className = "error-tag-highlight";
            }

            return {
              OrderId: order.slug,
              OrderName: order.shopify_order_name || order.slug,
              CreatedAt: order.created_at,
              CustomerName: order.customer_name || "",
              FulfillmentStatus: order.production_status || "unknown",
              Items: String(order.items || 0),
              Tag: order.tag || "",
              messageCount: order.message_count || 0,
              // Derived server-side from the order's line items — never from the
              // Note, which is the factory's own field and says nothing about
              // what the trainer requested.
              modifications: !!order.has_special_modifications,
              additionalInfo,
              additionalInfoClass: className,
            };
          });

          setOrders(processedOrders);
          setLoading(false);
        })
        .catch((err) => {
          console.error(err);
          setLoading(false);
        });
    } catch (error) {
      setLoading(false);
    }
  };

  // Fixed status options (known values)
  const uniqueFulfillmentStatuses = [
    "awaiting_review",
    "reviewed",
    "manufactured",
    "shipped",
    "cancelled",
  ];

  // Compute available tags from current page + already selected tags
  const uniqueTags = useMemo(() => {
    const allTags = orders.flatMap((order) =>
      order.Tag
        ? order.Tag.split(",")
            .map((tag) => (typeof tag === "string" ? tag.trim().toLowerCase() : tag))
            .filter(Boolean)
        : []
    );
    const fromPage = [...new Set(allTags)].filter(
      (tag) => tag && typeof tag === "string" && tag.trim() !== ""
    );
    // Include already-selected tags so they stay visible in the filter
    const combined = [...new Set([...fromPage, ...filters.tags])];
    return combined;
  }, [orders, filters.tags]);

  // Helper: update filters and re-fetch
  const applyNewFilters = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
    getAllOrders(1, perPage, searchTerm, newFilters);
  };

  // Filter handler functions
  const handleFulfillmentStatusFilter = (status) => {
    const newFilters = {
      ...filters,
      fulfillmentStatus: filters.fulfillmentStatus.includes(status)
        ? filters.fulfillmentStatus.filter((s) => s !== status)
        : [...filters.fulfillmentStatus, status],
    };
    applyNewFilters(newFilters);
  };

  const handleTagFilter = (tag) => {
    const newFilters = {
      ...filters,
      tags: filters.tags.includes(tag)
        ? filters.tags.filter((t) => t !== tag)
        : [...filters.tags, tag],
    };
    applyNewFilters(newFilters);
  };

  const toggleTagMode = () => {
    const newFilters = {
      ...filters,
      tagMode: filters.tagMode === "include" ? "exclude" : "include",
    };
    applyNewFilters(newFilters);
  };

  const handleTextFilter = (field, value) => {
    const newFilters = { ...filters, [field]: value };
    setFilters(newFilters);
    // Debounce handled separately for text inputs — don't re-fetch here
  };

  const handleAdditionalInfoFilter = (value) => {
    const newFilters = {
      ...filters,
      additionalInfo: filters.additionalInfo.includes(value)
        ? filters.additionalInfo.filter((v) => v !== value)
        : [...filters.additionalInfo, value],
    };
    applyNewFilters(newFilters);
  };

  const clearAllFilters = () => {
    const newFilters = {
      fulfillmentStatus: [],
      tags: [],
      tagMode: "include",
      customerName: "",
      items: "",
      additionalInfo: [],
    };
    applyNewFilters(newFilters);
  };

  // Orders are already filtered and sorted server-side
  const filteredAndSortedOrders = orders;

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      getAllOrders(1, perPage, searchTerm, filters);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Debounced customer name filter
  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      getAllOrders(1, perPage, searchTerm, filters);
    }, 400);
    return () => clearTimeout(timer);
  }, [filters.customerName]);

  useEffect(() => {
    getAllOrders(1, perPage, searchTerm, filters);
  }, []);

  return (
    <div className="container-fluid">
      {/* <div className="ForAndBak">
<div className="BackButton" onClick={() => navigate(-1)}><i className="fas fa-arrow-left backbtn"></i></div>
</div> */}

      <div className="d-sm-flex align-items-center justify-content-between mb-4">
        <h1 className="h3 mb-0 text-gray-800 HeadTitles">
          {lang === "en" ? "Orders" : "订单"}
        </h1>
      </div>

      <div className="row">
        <div className="col-xl-12 col-md-12 mb-12">
          <div className="card shadow mb-4 flaged">
            <div className="card-body orders-table">
              <div className="tableDropDown">
                <div className="DropdownTittle">
                  {lang === "en" ? "Order Status" : "订单状态"}
                </div>
                <select
                  className="custom-select"
                  value={OrderStatus}
                  onChange={changeHandler}
                >
                  {/* <option value="" disabled>
{lang === "en" ? "Order Status" : "订单状态"}
</option> */}
                  <option value="/orders">
                    {lang === "en" ? "All Orders" : "所有订单"}
                  </option>
                  <option value="/orders/awaiting_review">
                    {lang === "en" ? "Awaiting Review" : "等待审核"}
                  </option>
                  <option value="/orders/reviewed">
                    {lang === "en" ? "Reviewed" : "已审核"}
                  </option>
                  <option value="/orders/manufactured">
                    {lang === "en" ? "Manufactured" : "已生产"}
                  </option>
                  {/* <option value="/orders/quality-assured">
{lang === "en" ? "Quality Assured" : "质量已把关"}
</option>
<option value="/orders/packaging">
{lang === "en" ? "Packaging" : "包装"}
</option> */}
                  <option value="/orders/shipped">
                    {lang === "en" ? "Shipped" : "已发货"}
                  </option>
                  {/* <option value="/orders/fulfilled">
{lang === "en" ? "Fulfilled" : "實現了"}
</option> */}
                  {localStorage?.getItem("super_admin") === "true" && localStorage?.getItem("is_vendor") !== "true" && (
                    <option value="/orders/cancelled">
                      {lang === "en" ? "Cancelled" : "取消"}
                    </option>
                  )}
                </select>
              </div>

              {/* Multi-Column Filters */}
              <div className="row mb-3">
                <div className="col-12">
                  <div className="card">
                    <div
                      className="card-header"
                      style={{ cursor: "pointer" }}
                      onClick={() => setIsFiltersExpanded(!isFiltersExpanded)}
                    >
                      <h6 className="mb-0 d-flex justify-content-between align-items-center">
                        <span>
                          {lang === "en" ? "Advanced Filters" : "高级筛选"}
                          <i
                            className={`fas fa-chevron-${
                              isFiltersExpanded ? "up" : "down"
                            } ml-2`}
                          ></i>
                        </span>
                        <div className="d-flex align-items-center">
                          {/* Active Filters Count */}
                          {(filters.fulfillmentStatus.length > 0 ||
                            filters.tags.length > 0 ||
                            filters.customerName ||
                            filters.items ||
                            filters.additionalInfo.length > 0) && (
                            <span className="badge bg-primary me-2">
                              {filters.fulfillmentStatus.length +
                                filters.tags.length +
                                (filters.customerName ? 1 : 0) +
                                (filters.items ? 1 : 0) +
                                filters.additionalInfo.length}
                            </span>
                          )}
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={(e) => {
                              e.stopPropagation();
                              clearAllFilters();
                            }}
                          >
                            {lang === "en" ? "Clear All" : "清除所有"}
                          </button>
                        </div>
                      </h6>
                    </div>
                    {isFiltersExpanded && (
                      <div className="card-body">
                        <div className="row">
                          {/* Fulfillment Status Filter */}
                          <div className="col-md-3 mb-3">
                            <label className="form-label">
                              {lang === "en"
                                ? "Fulfillment Status"
                                : "进度状态"}
                            </label>
                            <div
                              className="filter-checkboxes"
                              style={{ maxHeight: "150px", overflowY: "auto" }}
                            >
                              {uniqueFulfillmentStatuses.map((status) => (
                                <div key={status} className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id={`fulfillment-${status}`}
                                    checked={filters.fulfillmentStatus.includes(
                                      status
                                    )}
                                    onChange={() =>
                                      handleFulfillmentStatusFilter(status)
                                    }
                                  />
                                  <label
                                    className="form-check-label"
                                    htmlFor={`fulfillment-${status}`}
                                  >
                                    {status === "OTHER"
                                      ? lang === "en"
                                        ? "Other"
                                        : "其他"
                                      : StatusConversion(status, lang)}
                                  </label>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Tags Filter */}
                          <div className="col-md-3 mb-3  gap-4">
                            <div className="d-flex justify-content-between align-items-center">
                              <label className="form-label">
                                {lang === "en" ? "Order Tags" : "供应商"}
                              </label>
                              <div
                                className="btn-group btn-group-sm mb-2 "
                                role="group"
                                aria-label="Tag mode"
                              >
                                <button
                                  type="button"
                                  className={`btn ${
                                    filters.tagMode === "include"
                                      ? "btn-primary"
                                      : "btn-outline-primary"
                                  }`}
                                  onClick={() => {
                                    if (filters.tagMode !== "include") {
                                      applyNewFilters({ ...filters, tagMode: "include" });
                                    }
                                  }}
                                >
                                  {lang === "en" ? "Include" : "包含"}
                                </button>
                                <button
                                  type="button"
                                  className={`btn ${
                                    filters.tagMode === "exclude"
                                      ? "btn-danger"
                                      : "btn-outline-danger"
                                  }`}
                                  onClick={() => {
                                    if (filters.tagMode !== "exclude") {
                                      applyNewFilters({ ...filters, tagMode: "exclude" });
                                    }
                                  }}
                                >
                                  {lang === "en" ? "Exclude" : "排除"}
                                </button>
                              </div>
                            </div>
                            <div
                              className="filter-checkboxes"
                              style={{ maxHeight: "150px", overflowY: "auto" }}
                            >
                              {uniqueTags.map((tag) => (
                                <div key={tag} className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id={`tag-${tag}`}
                                    checked={filters.tags.includes(tag)}
                                    onChange={() => handleTagFilter(tag)}
                                  />
                                  <label
                                    className="form-check-label"
                                    htmlFor={`tag-${tag}`}
                                  >
                                    {capitalizeWords(tag)}
                                  </label>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Customer Name + Items Filter */}
                          <div className="col-md-2 mb-3">
                            {/* Customer Name */}
                            <label className="form-label">
                              {lang === "en" ? "Customer Name" : "客户名称"}
                            </label>
                            <input
                              type="text"
                              className="form-control form-control-sm mb-2"
                              placeholder={
                                lang === "en"
                                  ? "Search customer..."
                                  : "搜索客户..."
                              }
                              value={filters.customerName}
                              onChange={(e) =>
                                handleTextFilter("customerName", e.target.value)
                              }
                            />

                            {/* Items */}
                            <label className="form-label">
                              {lang === "en" ? "Items" : "项目"}
                            </label>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              placeholder={
                                lang === "en"
                                  ? "Search items..."
                                  : "搜索项目..."
                              }
                              value={filters.items}
                              onChange={(e) =>
                                handleTextFilter("items", e.target.value)
                              }
                            />
                          </div>

                          {/* User Data Filter */}
                          <div className="col-md-2 mb-3">
                            <label className="form-label">
                              {lang === "en" ? "User Data" : "附加資訊"}
                            </label>
                            <div
                              className="filter-checkboxes"
                              style={{ maxHeight: "150px", overflowY: "auto" }}
                            >
                              {USER_DATA_OPTIONS.map((opt) => (
                                <div key={opt} className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id={`additional-${opt}`}
                                    checked={filters.additionalInfo.includes(
                                      opt
                                    )}
                                    onChange={() =>
                                      handleAdditionalInfoFilter(opt)
                                    }
                                  />
                                  <label
                                    className="form-check-label"
                                    htmlFor={`additional-${opt}`}
                                  >
                                    {opt}
                                  </label>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>

                        {/* Active Filters Display */}
                        {(filters.fulfillmentStatus.length > 0 ||
                          filters.tags.length > 0 ||
                          filters.customerName ||
                          filters.items ||
                          filters.additionalInfo.length > 0) && (
                          <div className="mt-3">
                            <small className="text-muted">
                              {lang === "en"
                                ? "Active Filters: "
                                : "活动筛选: "}
                            </small>
                            {filters.fulfillmentStatus.map((status) => (
                              <span
                                key={status}
                                className="d-inline-flex align-items-center rounded-pill px-3 py-1 me-2 shadow-sm bg-primary text-white"
                                style={{ fontSize: "1rem", fontWeight: 500 }}
                              >
                                {status === "OTHER"
                                  ? lang === "en"
                                    ? "Other"
                                    : "其他"
                                  : StatusConversion(status, lang)}
                                <span
                                  role="button"
                                  className="ms-2 d-flex align-items-center justify-content-center"
                                  style={{
                                    cursor: "pointer",
                                    fontSize: "1.1rem",
                                    lineHeight: "1",
                                    height: "1em",
                                  }}
                                  onClick={() =>
                                    handleFulfillmentStatusFilter(status)
                                  }
                                >
                                  ✖
                                </span>
                              </span>
                            ))}
                            {filters.tags.map((tag) => (
                              <span
                                key={tag}
                                className={`d-inline-flex align-items-center rounded-pill px-3 py-1 me-2 shadow-sm ${
                                  filters.tagMode === "include"
                                    ? "bg-info text-white"
                                    : "bg-danger text-white"
                                }`}
                                style={{ fontSize: "1rem", fontWeight: 500 }}
                              >
                                {capitalizeWords(tag)}
                                <span
                                  role="button"
                                  className="ms-2"
                                  style={{
                                    cursor: "pointer",
                                    fontSize: "1.1rem",
                                    lineHeight: 1,
                                  }}
                                  onClick={() => handleTagFilter(tag)}
                                >
                                  ✖
                                </span>
                              </span>
                            ))}

                            {/* Customer Name */}
                            {filters.customerName && (
                              <span
                                className="d-inline-flex align-items-center rounded-pill px-3 py-1 me-2 shadow-sm bg-secondary text-white"
                                style={{ fontSize: "1rem", fontWeight: 500 }}
                              >
                                Customer: {filters.customerName}
                                <span
                                  role="button"
                                  className="ms-2"
                                  style={{
                                    cursor: "pointer",
                                    fontSize: "1.1rem",
                                    lineHeight: 1,
                                  }}
                                  onClick={() =>
                                    handleTextFilter("customerName", "")
                                  }
                                >
                                  ✖
                                </span>
                              </span>
                            )}

                            {/* Items */}
                            {filters.items && (
                              <span
                                className="d-inline-flex align-items-center rounded-pill px-3 py-1 me-2 shadow-sm bg-secondary text-white"
                                style={{ fontSize: "1rem", fontWeight: 500 }}
                              >
                                Items: {filters.items}
                                <span
                                  role="button"
                                  className="ms-2"
                                  style={{
                                    cursor: "pointer",
                                    fontSize: "1.1rem",
                                    lineHeight: 1,
                                  }}
                                  onClick={() => handleTextFilter("items", "")}
                                >
                                  ✖
                                </span>
                              </span>
                            )}

                            {/* User Data */}
                            {filters.additionalInfo.map((opt) => (
                              <span
                                key={opt}
                                className="d-inline-flex align-items-center rounded-pill px-3 py-1 me-2 shadow-sm bg-success text-white"
                                style={{ fontSize: "1rem", fontWeight: 500 }}
                              >
                                {opt}
                                <span
                                  role="button"
                                  className="ms-2"
                                  style={{
                                    cursor: "pointer",
                                    fontSize: "1.1rem",
                                    lineHeight: 1,
                                  }}
                                  onClick={() =>
                                    handleAdditionalInfoFilter(opt)
                                  }
                                >
                                  ✖
                                </span>
                              </span>
                            ))}
                            {(filters.customerName ||
                              filters.items ||
                              filters.orderStatus ||
                              filters.fulfillmentStatus ||
                              filters.additionalInfo.length > 0) && (
                              <span
                                onClick={clearAllFilters}
                                style={{
                                  color: "#0d6efd", // Bootstrap primary blue
                                  cursor: "pointer",
                                  fontSize: "0.95rem",
                                  fontWeight: 500,
                                  marginLeft: "0.5rem", // added space before Clear All
                                }}
                              >
                                {lang === "en" ? "Clear All" : "清除所有"}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Server-side search */}
              <div className="mb-3">
                <label className="form-label">{customTableFilterLabel}</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder={lang === "en" ? "Search orders by name, customer, tag..." : "按名称、客户、标签搜索订单..."}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              <div className="custom-table">
                <CSmartTable
                  responsive="true"
                  items={filteredAndSortedOrders}
                  columns={columns}
                  columnSorter
                  loading={loading}
                  itemsPerPage={perPage}
                  tableProps={{
                    hover: true,
                    responsive: true,
                    striped: true,
                    className: "flagged-table",
                  }}
                  scopedColumns={{
                    id: (item, i) => {
                      return <td>{(currentPage - 1) * perPage + i + 1}.</td>;
                    },
                    OrderName: (items) => {
                      return (
                        <td>
                          <Link to={`/order/${items.OrderId}`}>
                            {items.OrderName}
                          </Link>
                        </td>
                      );
                    },
                    CreatedAt: (items) => {
                      return (
                        <td>
                          {changeGMTFormate(items.CreatedAt)}
                        </td>
                      );
                    },
                    messageCount: (items) => {
                      return (
                        <td>
                          {items.messageCount != "0" ? (
                            <div className="OrderMessageCount">
                              {items.messageCount}
                            </div>
                          ) : lang === "en" ? (
                            "No Message"
                          ) : (
                            "沒有留言"
                          )}
                        </td>
                      );
                    },
                    FulfillmentStatus: (items) => {
                      return (
                        <td>
                          {StatusConversion(items.FulfillmentStatus, lang)}
                        </td>
                      );
                    },
                    CustomerName: (items) => {
                      return (
                        <td>
                          {capitalizeWords(items.CustomerName)}
                        </td>
                      );
                    },
                    Items: (items) => {
                      return (
                        <td>
                          {capitalizeWords(items.Items)}
                        </td>
                      );
                    },
                    Tag: (items) => {
                      return (
                        <td>
                          {items?.Tag?.split(",").map((tag, index) => {
                            const trimmedTag = tag?.trim();
                            let tagClass = "";

                            if (trimmedTag.toLowerCase() === "test") {
                              tagClass = "test-tag-highlight";
                            } else if (trimmedTag.toLowerCase() === "rmu") {
                              tagClass = "rmu-tag-success";
                            }

                            return (
                              <span key={index} className={tagClass}>
                                {capitalizeWords(trimmedTag)}
                                {index < items?.Tag?.split(",").length - 1 &&
                                  ", "}
                              </span>
                            );
                          })}
                        </td>
                      );
                    },
                    additionalInfo: (items) => (
                      <td>
                        <span className={items?.additionalInfoClass}>
                          {items?.additionalInfo}
                        </span>
                      </td>
                    ),
                    modifications: (items) => (
                      <td>
                        {items?.modifications ? (
                          <span className="modifications-flag-highlight">
                            {lang === "en" ? "YES" : "是"}
                          </span>
                        ) : (
                          ""
                        )}
                      </td>
                    ),
                  }}
                />

                {/* Server-side pagination controls */}
                {totalPages > 1 && (
                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <div>
                      <label className="me-2">{customItemsPerPageLabel}</label>
                      <select
                        className="form-select form-select-sm d-inline-block"
                        style={{ width: "auto" }}
                        value={perPage}
                        onChange={(e) => {
                          const newPerPage = Number(e.target.value);
                          setPerPage(newPerPage);
                          setCurrentPage(1);
                          getAllOrders(1, newPerPage, searchTerm);
                        }}
                      >
                        {[10, 20, 50, 100].map((n) => (
                          <option key={n} value={n}>{n}</option>
                        ))}
                      </select>
                      <span className="ms-3 text-muted">
                        {lang === "en"
                          ? `Showing ${(currentPage - 1) * perPage + 1}–${Math.min(currentPage * perPage, totalOrders)} of ${totalOrders}`
                          : `显示 ${(currentPage - 1) * perPage + 1}–${Math.min(currentPage * perPage, totalOrders)} 共 ${totalOrders}`}
                      </span>
                    </div>
                    <nav>
                      <ul className="pagination mb-0">
                        <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                          <button className="page-link" onClick={() => { setCurrentPage(1); getAllOrders(1, perPage, searchTerm); }}>«</button>
                        </li>
                        <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                          <button className="page-link" onClick={() => { const p = currentPage - 1; setCurrentPage(p); getAllOrders(p, perPage, searchTerm); }}>‹</button>
                        </li>
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          let page;
                          if (totalPages <= 5) {
                            page = i + 1;
                          } else if (currentPage <= 3) {
                            page = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            page = totalPages - 4 + i;
                          } else {
                            page = currentPage - 2 + i;
                          }
                          return (
                            <li key={page} className={`page-item ${currentPage === page ? "active" : ""}`}>
                              <button className="page-link" onClick={() => { setCurrentPage(page); getAllOrders(page, perPage, searchTerm); }}>{page}</button>
                            </li>
                          );
                        })}
                        <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                          <button className="page-link" onClick={() => { const p = currentPage + 1; setCurrentPage(p); getAllOrders(p, perPage, searchTerm); }}>›</button>
                        </li>
                        <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                          <button className="page-link" onClick={() => { setCurrentPage(totalPages); getAllOrders(totalPages, perPage, searchTerm); }}>»</button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrdersPage;
